import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { noteLinks } from "../lib/orderEdits";
import { useTRPC } from "../lib/trpc";

/**
 * TCG Fulfill Order's notes: free text saved with Save Notes at any status, no confirm, and the
 * links in it listed as Note Links. Saving empty clears the notes. The tenant is told
 * (`metadata_changed`).
 */
export function OrderNotesEditor({ orderId, notes }: { orderId: string; notes: string | null }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation(
    trpc.platform.fulfillment.orders.updateMetadata.mutationOptions({
      onSuccess: () => {
        toast.success("Notes saved.");
        setDraft(null);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );
  const links = noteLinks(notes);

  return (
    <section className="flex flex-col gap-2 lg:col-span-6">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Notes</h2>
        {draft === null ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setDraft(notes ?? "")}>
            Edit Notes
          </Button>
        ) : null}
      </div>
      {draft === null ? (
        <p className="min-h-16 rounded-md border p-2 text-sm whitespace-pre-wrap">
          {notes || <span className="text-muted-foreground">No notes.</span>}
        </p>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ id: orderId, notes: draft });
          }}
        >
          <Textarea
            aria-label="Notes"
            value={draft}
            maxLength={5000}
            rows={5}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save Notes"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {links.length > 0 ? (
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">Note Links</span>
          {links.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="break-all underline underline-offset-4"
            >
              {url}
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
}
